import { internalPersonLicenseDataChangeSchema, internalPersonOccupationDataChangeSchema, oigStatuses, personOccupationSchema, } from '../../../../backend/extraTypes';
import { accountSchema, AddressSchema } from '@juvare/contact-management/backend/features/accounts/shared';
import { defineColumns } from '@juvare/platform-shared/features/storage';
import { adjustZodSchemaDefaultForUnknownFields, zodKnownFieldDefaultValidation } from '@juvare/platform-shared/features/validation';
import { z } from 'zod';
export const extendedAddressSchema = AddressSchema.merge(z.object({
    tribeId: z.string().uuid().optional(),
    county: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
    residenceType: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
    state: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
}));
const validPassportSchema = z.object({
    validPassport: z.boolean().optional(),
    passportNumber: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
});
export const personCredentialsSchema = adjustZodSchemaDefaultForUnknownFields(accountSchema.merge(z.object({
    middleName: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
    dateOfBirth: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
    driversLicense: z
        .object({
        licenseNumber: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        skillIds: z
            .string()
            .max(zodKnownFieldDefaultValidation.stringMax)
            .array()
            .max(zodKnownFieldDefaultValidation.arrayMax)
            .optional(),
        hazmat: z.boolean().optional(),
        licenseNumberDecrypted: z.boolean().optional(),
        licenseIssuingStateProvince: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
    })
        .optional(),
    ssn: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
    occupations: personOccupationSchema.array().max(zodKnownFieldDefaultValidation.arrayMax).optional(),
    internalPersonLicenseDataChange: internalPersonLicenseDataChangeSchema.optional(),
    oigStatus: z.enum(oigStatuses).optional(),
    licenseCheckRequestDate: z.number().optional(),
    /**
     * Is used when enqueueing/reading platform messages for the
     * OIG verification not as the occupation credential.
     */
    oigVerificationMessageId: z.string().uuid().optional(),
    address: extendedAddressSchema.optional(),
    skillsAndCertifications: z
        .object({
        healthcareSkillIds: z.string().uuid().array().max(zodKnownFieldDefaultValidation.arrayMax).optional(),
        otherSkillIds: z.string().uuid().array().max(zodKnownFieldDefaultValidation.arrayMax).optional(),
    })
        .optional(),
    languages: z
        .object({
        languageId: z.string().uuid().optional(),
        spokenAbilityId: z.string().uuid().optional(),
        writtenAbilityId: z.string().uuid().optional(),
    })
        .array()
        .max(zodKnownFieldDefaultValidation.arrayMax)
        .optional(),
    trainings: z
        .object({
        courseDate: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        courseId: z.string().uuid().optional(),
        institution: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        noExpirationDate: z.boolean().optional(),
        expirationDate: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        verificationNotes: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        verificationStatus: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
    })
        .array()
        .max(zodKnownFieldDefaultValidation.arrayMax)
        .optional(),
    willingnessAndAvailability: z
        .object({
        deploymentDurationId: z.string().uuid().optional(),
        workUnderFederalGovernment: z.boolean().optional(),
        deploymentTravelIds: z.string().uuid().array().max(zodKnownFieldDefaultValidation.arrayMax).optional(),
        covid19Vaccine: z.boolean().optional(),
        medicalOrReligiousExemption: z.boolean().optional(),
        haveOtherCommitments: z.boolean().optional(),
        organizationsIds: z.string().uuid().array().max(zodKnownFieldDefaultValidation.arrayMax).optional(),
        deployable: z.boolean().optional(),
    })
        .merge(validPassportSchema)
        .optional(),
    stateAndCounty: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
    trainUsername: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
    internalPersonOccupationDataChange: internalPersonOccupationDataChangeSchema.optional(),
})));
export const personCredentialsColumns = defineColumns({
    occupationIds: {
        type: 'id-array-required',
        index: true,
    },
    trainUsername: {
        type: 'string',
        index: true,
    },
    occupationGroups: {
        type: 'id-array-required',
        index: true,
    },
    occupationsCount: {
        type: 'number-required',
        index: true,
    },
    ecls: {
        type: 'string-array-required',
        index: true,
    },
    occupationEcls: {
        // occupationId_ecl
        type: 'string-array-required',
        index: true,
    },
    licenseCheckRequestDate: {
        type: 'number',
    },
    oigVerificationMessageId: {
        type: 'string',
    },
    deploymentTravelIds: {
        type: 'string-array-required',
        index: true,
    },
    deploymentDuration: {
        type: 'number', // storing number value of duration
        index: true,
    },
    trainingCourseIds: {
        type: 'string-array-required',
        index: true,
    },
    languageIds: {
        type: 'string-array-required',
        index: true,
    },
    skillIds: {
        type: 'string-array-required',
        index: true,
    },
    workUnderFederalGovernment: {
        type: 'string',
        index: true,
    },
    priorCommitment: {
        type: 'string',
        index: true,
    },
    // TODO Move state, county and tribe to contact-management
    state: {
        type: 'string',
        index: true,
    },
    county: {
        type: 'string',
        index: true,
    },
    tribeId: {
        type: 'string',
        index: true,
    },
    trainingStatus: {
        type: 'string-array', // trainingId_trainingStatus
        index: true,
    },
    trainingStatusSet: {
        type: 'string-array', // trainingStatus
        index: true,
    },
    occupationInformationIds: {
        type: 'string-array-required', // occupationId_credentialId_licenseId
        index: true,
    },
    deploymentDurationId: {
        type: 'string',
        index: true,
    },
    stateAndCounty: {
        type: 'string',
        index: true,
    },
    credentialNames: {
        type: 'string-array',
    },
    licenseStatuses: {
        type: 'string-array',
    },
    oigStatus: {
        type: 'string',
    },
});
